import { parseTaskDate } from '../../../utils/formatDate'
import { getTaskCreatorIds } from './taskUsers'

export type TaskFilterField = '' | 'priority' | 'status' | 'createdAt' | 'dueDate' | 'author'
export type TaskFilterOperator = 'is' | 'is_not'

export interface TaskFilterRule {
    id: string
    field: TaskFilterField
    operator: TaskFilterOperator
    value: string | string[]
}

export interface TaskFilterState {
    rules: TaskFilterRule[]
}

export const EMPTY_TASK_FILTER_STATE: TaskFilterState = {
    rules: [],
}

const TASK_FILTER_FIELDS = new Set<TaskFilterField>(['', 'priority', 'status', 'createdAt', 'dueDate', 'author'])
const TASK_FILTER_OPERATORS = new Set<TaskFilterOperator>(['is', 'is_not'])

export function parseTaskFilterState(value: unknown): TaskFilterState {
    if (!value || typeof value !== 'object') return { ...EMPTY_TASK_FILTER_STATE }

    const storedRules = (value as { rules?: unknown }).rules
    if (!Array.isArray(storedRules)) return { ...EMPTY_TASK_FILTER_STATE }

    const rules = storedRules.flatMap((storedRule, index): TaskFilterRule[] => {
        if (!storedRule || typeof storedRule !== 'object') return []

        const rule = storedRule as Partial<TaskFilterRule>
        if (!TASK_FILTER_FIELDS.has(rule.field as TaskFilterField)) return []

        return [{
            id: typeof rule.id === 'string' && rule.id ? rule.id : `stored-filter-${index}`,
            field: rule.field as TaskFilterField,
            operator: TASK_FILTER_OPERATORS.has(rule.operator as TaskFilterOperator)
                ? rule.operator as TaskFilterOperator
                : 'is',
            value: Array.isArray(rule.value)
                ? Array.from(new Set(rule.value.filter((item) => item != null).map(String)))
                : rule.value == null ? '' : String(rule.value),
        }]
    })

    return { rules }
}

export function readTaskFilterState(projectId?: string): TaskFilterState {
    if (!projectId || typeof window === 'undefined') return { ...EMPTY_TASK_FILTER_STATE }

    try {
        const saved = window.localStorage.getItem(`project-task-filters:${projectId}`)
        return saved ? parseTaskFilterState(JSON.parse(saved)) : { ...EMPTY_TASK_FILTER_STATE }
    } catch {
        return { ...EMPTY_TASK_FILTER_STATE }
    }
}

export function writeTaskFilterState(projectId: string | undefined, state: TaskFilterState) {
    if (!projectId || typeof window === 'undefined') return

    try {
        window.localStorage.setItem(`project-task-filters:${projectId}`, JSON.stringify(state))
    } catch {
        // The active filter remains usable when browser storage is unavailable.
    }
}

const getDateValue = (value: unknown) => {
    if (!value) return ''
    const date = parseTaskDate(String(value))
    if (!date) return ''

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getFieldValue = (task: any, field: Exclude<TaskFilterField, ''>): string | string[] => {
    switch (field) {
        case 'priority': return String(task?.priority || '').toLowerCase()
        case 'status': return String(task?.status || task?.status_key || '')
        case 'createdAt': return getDateValue(task?.created_at || task?.createdAt)
        case 'dueDate': return getDateValue(task?.due_date || task?.dueDate || task?.deadline)
        case 'author': return getTaskCreatorIds(task)
        default: return ''
    }
}

const matchesRule = (task: any, rule: TaskFilterRule) => {
    const filterValues = Array.isArray(rule.value)
        ? rule.value
        : rule.value ? [rule.value] : []
    if (!rule.field || filterValues.length === 0) return true

    const fieldValue = getFieldValue(task, rule.field)
    const matches = filterValues.some((filterValue) => (
        Array.isArray(fieldValue)
            ? fieldValue.includes(String(filterValue))
            : fieldValue === String(filterValue)
    ))
    return rule.operator === 'is_not' ? !matches : matches
}

export function hasTaskFilterValue(rule: TaskFilterRule) {
    return Array.isArray(rule.value) ? rule.value.length > 0 : Boolean(rule.value)
}

export function applyTaskFilters(tasks: any[], state: TaskFilterState) {
    return tasks.filter((task) => state.rules.every((rule) => matchesRule(task, rule)))
}
